import { useCallback, useEffect, useState } from 'react';
import { ExternalLink, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { Spinner } from '@/shared/components/Spinner';
import { getErrorMessage } from '@/shared/services/api';
import { FEED_ITEM_LINK_DESCRIPTION_MAX, FeedItemLinkDto } from '@/shared/types/api';
import { cn } from '@/shared/utils/cn';
import { isValidHttpUrl } from '@/shared/utils/url';
import { feedItemLinkApi } from '../services/feedItemLinkApi';

interface UsefulLinksPanelProps {
  feedItemId: number;
  currentUserId: string;
  isAdmin?: boolean;
  /** Called after any mutation so the caller can refresh its link-count badge. */
  onCountChange?: (count: number) => void;
}

interface DraftState {
  url: string;
  description: string;
}

const EMPTY_DRAFT: DraftState = { url: '', description: '' };

/**
 * View, add, edit and delete the useful links attached to a feed item
 * (SC-237 / SC-275 / SC-276). Rendered as an inline expandable panel below the feed
 * item — matching the comments panel — so links and comments can be open at the same
 * time and the user keeps the context of the current screen (SC-291).
 */
export function UsefulLinksPanel({
  feedItemId,
  currentUserId,
  isAdmin = false,
  onCountChange,
}: UsefulLinksPanelProps) {
  const [links, setLinks] = useState<FeedItemLinkDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [draft, setDraft] = useState<DraftState>(EMPTY_DRAFT);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editDraft, setEditDraft] = useState<DraftState>(EMPTY_DRAFT);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const result = await feedItemLinkApi.list(feedItemId);
      setLinks(result);
      onCountChange?.(result.length);
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to load useful links'));
    } finally {
      setLoading(false);
    }
  }, [feedItemId, onCountChange]);

  useEffect(() => {
    void load();
  }, [load]);

  const canModify = (link: FeedItemLinkDto) => isAdmin || link.userId === currentUserId;

  const validate = ({ url, description }: DraftState): string | null => {
    if (!url.trim()) return 'Enter a URL';
    if (!isValidHttpUrl(url)) return 'Enter an absolute http:// or https:// URL';
    if (!description.trim()) return 'Enter a description';
    if (description.trim().length > FEED_ITEM_LINK_DESCRIPTION_MAX) {
      return `Description cannot exceed ${FEED_ITEM_LINK_DESCRIPTION_MAX} characters`;
    }
    return null;
  };

  const handleAdd = async () => {
    const error = validate(draft);
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const created = await feedItemLinkApi.create({
        feedItemId,
        url: draft.url.trim(),
        description: draft.description.trim(),
      });
      const next = [...links, created];
      setLinks(next);
      onCountChange?.(next.length);
      setDraft(EMPTY_DRAFT);
      toast.success('Useful link added');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to add useful link'));
    } finally {
      setSaving(false);
    }
  };

  const startEdit = (link: FeedItemLinkDto) => {
    setEditingId(link.id);
    setEditDraft({ url: link.url, description: link.description });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDraft(EMPTY_DRAFT);
  };

  const handleUpdate = async (id: number) => {
    const error = validate(editDraft);
    if (error) {
      toast.error(error);
      return;
    }

    setSaving(true);
    try {
      const updated = await feedItemLinkApi.update(id, {
        url: editDraft.url.trim(),
        description: editDraft.description.trim(),
      });
      setLinks((prev) => prev.map((l) => (l.id === id ? updated : l)));
      cancelEdit();
      toast.success('Useful link updated');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to update useful link'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await feedItemLinkApi.remove(id);
      const next = links.filter((l) => l.id !== id);
      setLinks(next);
      onCountChange?.(next.length);
      if (editingId === id) cancelEdit();
      toast.success('Useful link deleted');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to delete useful link'));
    } finally {
      setSaving(false);
    }
  };

  const remaining = FEED_ITEM_LINK_DESCRIPTION_MAX - draft.description.length;

  return (
    <section aria-label="Useful Links">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold text-foreground">
        <Link2 className="h-4 w-4 text-primary" />
        Useful Links
      </h3>

      {/* Existing links (SC-275 view) */}
      <div className="space-y-3">
        {loading ? (
          <div className="flex items-center gap-2 py-4 text-sm text-muted-foreground">
            <Spinner />
            Loading useful links…
          </div>
        ) : links.length === 0 ? (
          <p className="py-2 text-sm text-muted-foreground">
            No useful links yet. Add one below to give this item supporting context.
          </p>
        ) : (
          <ul className="space-y-2">
            {links.map((link) =>
              editingId === link.id ? (
                <li key={link.id} className="rounded-md border border-primary/40 bg-primary/5 p-3">
                  <div className="space-y-2">
                    <div>
                      <label className="label mb-1 block" htmlFor={`edit-url-${link.id}`}>
                        URL
                      </label>
                      <input
                        id={`edit-url-${link.id}`}
                        type="url"
                        value={editDraft.url}
                        onChange={(e) => setEditDraft((d) => ({ ...d, url: e.target.value }))}
                        className="input w-full"
                        placeholder="https://example.com/page"
                      />
                    </div>
                    <div>
                      <label className="label mb-1 block" htmlFor={`edit-desc-${link.id}`}>
                        Description
                      </label>
                      <input
                        id={`edit-desc-${link.id}`}
                        type="text"
                        value={editDraft.description}
                        maxLength={FEED_ITEM_LINK_DESCRIPTION_MAX}
                        onChange={(e) =>
                          setEditDraft((d) => ({ ...d, description: e.target.value }))
                        }
                        className="input w-full"
                      />
                    </div>
                    <div className="flex gap-2 pt-1">
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="btn-secondary btn-sm flex-1"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={() => handleUpdate(link.id)}
                        disabled={saving}
                        className="btn-primary btn-sm flex-1"
                      >
                        {saving ? 'Saving…' : 'Save'}
                      </button>
                    </div>
                  </div>
                </li>
              ) : (
                <li
                  key={link.id}
                  className="flex items-start justify-between gap-2 rounded-md border border-border bg-surface p-3"
                >
                  <div className="min-w-0">
                    <a
                      href={link.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-1 text-sm font-medium text-primary hover:underline"
                    >
                      <span className="truncate">{link.description}</span>
                      <ExternalLink className="h-3 w-3 shrink-0" />
                    </a>
                    <p className="truncate text-xs text-muted-foreground" title={link.url}>
                      {link.url}
                    </p>
                    {link.authorName && (
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        Added by {link.authorName}
                        {link.isEdited && ' · edited'}
                      </p>
                    )}
                  </div>
                  {canModify(link) && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        type="button"
                        onClick={() => startEdit(link)}
                        className="btn-icon btn-ghost btn-sm text-muted-foreground"
                        title="Edit link"
                        aria-label={`Edit link ${link.description}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(link.id)}
                        disabled={saving}
                        className="btn-icon btn-ghost btn-sm text-destructive"
                        title="Delete link"
                        aria-label={`Delete link ${link.description}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  )}
                </li>
              )
            )}
          </ul>
        )}
      </div>

      {/* Add form (SC-237) */}
      <div className="mt-4 space-y-3 border-t border-border pt-4">
        <div>
          <label className="label mb-1 block" htmlFor={`new-link-url-${feedItemId}`}>
            URL
          </label>
          <input
            id={`new-link-url-${feedItemId}`}
            type="url"
            value={draft.url}
            onChange={(e) => setDraft((d) => ({ ...d, url: e.target.value }))}
            className="input w-full"
            placeholder="https://example.com/page"
          />
        </div>
        <div>
          <div className="mb-1 flex items-center justify-between">
            <label className="label" htmlFor={`new-link-description-${feedItemId}`}>
              Description
            </label>
            <span
              className={cn('text-xs', remaining < 0 ? 'text-destructive' : 'text-muted-foreground')}
            >
              {remaining} left
            </span>
          </div>
          <input
            id={`new-link-description-${feedItemId}`}
            type="text"
            value={draft.description}
            maxLength={FEED_ITEM_LINK_DESCRIPTION_MAX}
            onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
            className="input w-full"
            placeholder="What is this link for?"
          />
        </div>
        <button
          type="button"
          onClick={handleAdd}
          disabled={saving}
          className="btn-primary btn-sm w-full"
        >
          {saving ? <Spinner className="text-primary-foreground" /> : <Plus className="h-4 w-4" />}
          {saving ? 'Saving…' : 'Add link'}
        </button>
      </div>
    </section>
  );
}
